import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import Navbar from '../../components/navbar/navbar'
import GridProperty from '../../components/grid-property'
import DetailSidebarThree from '../../components/detail-sidebar-three'
import FooterTop from '../../components/footer-top'
import Footer from '../../components/footer'

import agencyImg from '../../assets/img/ag-1.png'
import userImg from '../../assets/img/user-5.jpg'

import { API_BASE } from '../../utils/api'

interface DBProperty {
    _id: string; title: string; price: number; status: string; type: string;
    city?: string; address?: string; bedrooms?: number; bathrooms?: number; area?: string;
    images?: string[]; isActive: boolean; isFeatured?: boolean; createdAt: string;
    owner?: { name: string; role: string };
}

interface DBAgent {
    _id: string; name: string; email: string; role: string; phone?: string;
    about?: string; city?: string; state?: string; avatar?: string;
    facebook?: string; twitter?: string; googleplus?: string; linkedin?: string;
}

export default function AgencyPage() {
    const [activeTab, setActiveTab] = useState<number>(1)
    const [agency, setAgency] = useState<DBAgent | null>(null)
    const [properties, setProperties] = useState<DBProperty[]>([])
    const [loading, setLoading] = useState(true)

    const params = useParams()
    const id = params.id

    useEffect(() => {
        if (!id) return;
        setLoading(true)
        fetch(`${API_BASE}/users/agents/${id}`)
            .then(r => r.json())
            .then(data => {
                if (data.agent) {
                    setAgency(data.agent)
                    setProperties(data.properties || [])
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [id])

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Loading agency details...</p>
            </div>
        )
    }

    if (!agency) {
        return (
            <div className="text-center py-5">
                <h3>Agency not found</h3>
                <Link to="/agents" className="btn btn-primary mt-3">Back to Agencies</Link>
            </div>
        )
    }

    return (
        <>
            <Navbar transparent={false} />

            <div className="image-cover page-title">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            <h2 className="ipt-title">Agency Detail</h2>
                            <span className="ipn-subtitle">{agency.name} {agency.city ? `from ${agency.city}` : ''}</span>
                        </div>
                    </div>
                </div>
            </div>

            <section className="agent-page p-0 gray-simple">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            <div className="agency agency-list overlio-40 bg-white shadow-sm rounded">
                                <div className="agency-avatar">
                                    <img src={agency.avatar || agencyImg} alt={agency.name} />
                                </div>
                                <div className="agency-content">
                                    <div className="agency-name">
                                        <h4>{agency.name}</h4>
                                        <span><i className="fa-solid fa-location-dot me-1"></i>{agency.city || 'Location'}, {agency.state || 'State'}</span>
                                    </div>
                                    <div className="agency-desc">
                                        <p>{agency.about || `Welcome to ${agency.name}. We are a professional real estate agency committed to providing exceptional service and helping you find the perfect property.`}</p>
                                    </div>
                                    <div className="prt-detios">
                                        <span className="label text-light bg-success">{properties.length} Properties</span>
                                    </div>
                                    <ul className="social-icons mt-4">
                                        {agency.facebook && <li><a className="facebook" href={agency.facebook}><i className="fa-brands fa-facebook"></i></a></li>}
                                        {agency.twitter && <li><a className="twitter" href={agency.twitter}><i className="fa-brands fa-twitter"></i></a></li>}
                                        {agency.linkedin && <li><a className="linkedin" href={agency.linkedin}><i className="fa-brands fa-linkedin"></i></a></li>}
                                    </ul>
                                    <div className="clearfix"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="gray">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 col-md-12 col-sm-12">
                            <div className="block-wrap bg-white mb-4 rounded">
                                <div className="block-header ags p-4 border-bottom d-flex align-items-center justify-content-between">
                                    <h4 className="block-title mb-0">Agency Info</h4>
                                    <Link to="/agent-dashboard" className="btn btn-seegreen">Go to Dashboard</Link>
                                </div>
                                <div className="block-body p-4">
                                    <ul className="dw-proprty-info list-unstyled mb-0">
                                        <li><strong>Name:</strong> {agency.name}</li>
                                        <li><strong>Email:</strong> {agency.email}</li>
                                        <li><strong>Phone:</strong> {agency.phone || 'Not available'}</li>
                                        <li><strong>Location:</strong> {agency.city || 'City'}, {agency.state || 'State'}</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="block-wraps bg-white rounded">
                                <div className="block-wraps-header">
                                    <div className="block-header p-3 border-bottom">
                                        <ul className="nav nav-pills py-2" id="pills-tab" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link px-4 fw-medium ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}>Details</button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link px-4 fw-medium ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}>Properties ({properties.length})</button>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="block-body p-4">
                                        <div className="tab-content" id="pills-tabContent">
                                            <div className={`tab-pane fade ${activeTab === 1 ? 'show active' : ''}`} id="agent">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <h5>About the Agency</h5>
                                                        <p>{agency.about || 'No detailed description available for this agency yet.'}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`tab-pane fade ${activeTab === 2 ? 'show active' : ''}`} id="property">
                                                <div className="row justify-content-center g-4">
                                                    {properties.length > 0 ? (
                                                        properties.map((item) => (
                                                            <div className="col-lg-6 col-md-6 col-sm-12" key={item._id}>
                                                                <GridProperty item={item} border={true} />
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="col-12 text-center py-4 text-muted">No properties listed by this agency.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12 col-sm-12">
                            <DetailSidebarThree />
                        </div>
                    </div>
                </div>
            </section>

            <FooterTop bg="theme-bg" />
            <Footer />
        </>
    )
}

